import registry from '~/lib/brandModels.json';

/**
 * Per-brand model registry powering the 型號 experience on vendor PLPs
 * (referencing scorpionexo.com's model selector).
 *
 * The filter itself is the `custom.model` product metafield (list type,
 * backfilled across the brand's products from title matching), surfaced
 * through Shopify Search & Discovery like every other metafield facet on
 * this store — id `filter.p.m.custom.model` with real per-model counts. The
 * sidebar's only special-casing is presentational: it folds the values under
 * their parent series (see groupBySeries). Until the filter is added in the
 * Search & Discovery app the facet simply doesn't appear.
 *
 * A model is named the way its brand names it, because that is what the
 * shopper is holding: SCORPION and most others by the model name, KUSHITANI
 * by the product code its own catalogue and every product title carry —
 * `K-2423 FULL MESH JACKET`, which is also what separates the two jackets
 * that share that name. Whatever the form, an entry's `name` must match the
 * product's `custom.model` value character for character or the band below
 * never resolves.
 *
 * This registry adds what Shopify doesn't hold: each model's copy and banner
 * for the band above the product count. Edit copy here; for banners either
 * drop images into public/images/models/<brand>/ or paste a Shopify Files
 * CDN URL, then fill in `banner`.
 */
export interface BrandModel {
  name: string;
  slug: string;
  /**
   * One sentence, and deliberately short: this doubles as the page's meta
   * description, which `truncate` caps at 155 characters once the brand
   * prefix is added. Long-form copy belongs in detail_*, which nothing
   * truncates.
   */
  intro_zh: string;
  intro_en: string;
  /** On-page paragraphs for the band above the product count. */
  detail_zh: string[];
  detail_en: string[];
  /**
   * The model's hero image, rendered at the foot of the band in a fixed 8:3
   * box with object-cover — so supply every model the same shape and they
   * all occupy identical space. Either a path under public/
   * (/images/models/scorpion/<slug>.jpg) or an absolute CDN URL. Empty
   * means no banner; a path that 404s is hidden rather than shown broken.
   */
  banner: string;
}

const REGISTRY = registry as Record<string, BrandModel[]>;

export const MODEL_FACET_ID = 'filter.p.m.custom.model';

export function modelsForVendor(vendor: string): BrandModel[] {
  const key = Object.keys(REGISTRY).find(
    (k) => k.toLowerCase() === vendor.trim().toLowerCase(),
  );
  return key ? REGISTRY[key] : [];
}

/**
 * The model the URL currently selects, or null.
 *
 * The facet is multi-select (ticking a second model appends a param rather
 * than replacing the first), and the resulting grid is the union of both
 * models. Returning the first match would caption that mixed grid with one
 * model's name and copy — and which one depended on param order. So the band
 * only speaks for the page when exactly one model resolves.
 */
export function selectedBrandModel(
  models: BrandModel[],
  searchParams: URLSearchParams,
): BrandModel | null {
  const hits: BrandModel[] = [];
  for (const raw of searchParams.getAll(MODEL_FACET_ID)) {
    try {
      const parsed = JSON.parse(raw) as {
        productMetafield?: {namespace?: string; key?: string; value?: string};
      };
      const mf = parsed.productMetafield;
      if (mf?.namespace !== 'custom' || mf?.key !== 'model') continue;
      const hit = models.find((m) => m.name === mf.value);
      // The same model repeated in the URL is still one model.
      if (hit && !hits.some((h) => h.slug === hit.slug)) hits.push(hit);
    } catch {
      // malformed param — ignore
    }
  }
  return hits.length === 1 ? hits[0] : null;
}

/**
 * The series a model name belongs to: its first whitespace-delimited token.
 *
 * SCORPION names every derivative by appending to the base name — EXO-1400,
 * EXO-1400 AIR, EXO-1400 EVO AIR; VX-16 AIR, VX-16 EVO AIR — so the leading
 * token is the series across the whole catalogue, and a name with no space
 * is a series of its own. Change here if a brand ever breaks that rule.
 */
export function modelSeries(name: string): string {
  const trimmed = name.trim();
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

export interface ModelSeriesGroup<T> {
  series: string;
  members: T[];
}

/**
 * Tokens that never head a series, however many models begin with them.
 *
 * The first-token rule reads that token as a family stem, which holds for a
 * brand that suffixes its qualifiers — SCORPION's EXO-1400 becomes EXO-1400
 * AIR. Several brands prefix them instead, and then the stem is a feature
 * word describing unrelated products: ROUGH AND ROAD's AIR heads a softshell
 * parka and a vented one, JET pairs a mesh jacket with a knee protector,
 * MULTI a neck gaiter with a pouch, and ROUGH — the brand's own name —
 * gloves with trainers. Every entry below is a grouping the live catalogue
 * produced and a shopper would have found meaningless.
 *
 * A stopped value keeps its full name as its own key, so it renders as an
 * ordinary flat row: the sidebar only ever folds what genuinely belongs
 * together.
 */
const SERIES_STOPWORDS = new Set(['AIR', 'JET', 'MULTI', 'ROUGH']);

/**
 * Buckets 型號 facet rows under their series, in the order each series first
 * appears. Shopify lists facet values alphabetically, so a series lands where
 * its base model sits and its members follow in that same order.
 */
export function groupBySeries<T extends {label: string}>(
  values: T[],
): ModelSeriesGroup<T>[] {
  const groups = new Map<string, ModelSeriesGroup<T>>();
  for (const value of values) {
    const token = modelSeries(value.label);
    const stopped = SERIES_STOPWORDS.has(token.toUpperCase());
    const series = stopped ? value.label.trim() : token;
    // A stopped token keys on the whole name, so nothing else can join it.
    const key = stopped ? `=${series.toUpperCase()}` : series.toUpperCase();
    const group = groups.get(key);
    if (group) group.members.push(value);
    else groups.set(key, {series, members: [value]});
  }
  return [...groups.values()];
}
